import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { AccountAPIService } from './account-api.service';
import { IsLoadingService } from '../is-loading.service';
import { ThemeMode } from '../theme/theme.service';
import {
  BehaviorSubject,
  catchError,
  filter,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import {
  OutkeepSettings,
  UpdateUserPayload,
  User,
  UserSettings,
} from '../../types/User';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  CURRENT_USER_LOCAL_STORAGE,
  MOBILE_REQUESTS_LIST_COMPACT_MODE,
  TUI_THEME_DEFAULT_KEY,
  X_TABLE_COLUMNS,
  X_TABLE_COLUMNS_ORDER,
  X_TABLE_COLUMNS_WIDTHS,
  X_TABLE_ROWS_PER_PAGE,
  X_TABLE_ROWS_SETTINGS,
} from '@core/constants';
import { LocalStorageService } from '@core/services/local-storage.service';
import { deepMerge } from '@core/helpers';
import { normalizeOutkeepTableSettings } from '@core/helpers';
import { AuthLoginService } from '@core/services/auth-login.service';
import { Counterparty } from '@core/types';
import type { AuthState } from './account.types';
import { OfflineSnapshotRepository } from '../offline/offline-snapshot.repository';
import { WorkflowStorageRepository } from '../workflow/storage/workflow-storage.repository';
import { environment } from '@environment/environment';
import * as Sentry from '@sentry/angular';

const OFFLINE_SESSION_TTL_MINUTES = 24 * 60;

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  private readonly accountAPIService = inject(AccountAPIService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly themeChanged = new BehaviorSubject<ThemeMode | null>(null);
  private readonly isLoadingService = inject(IsLoadingService);
  private readonly user$ = new BehaviorSubject<User | null>(null);
  private readonly authState$$ = new BehaviorSubject<AuthState>('unknown');
  private readonly authLoginService = inject(AuthLoginService);
  private readonly workflowStorageRepository = inject(
    WorkflowStorageRepository,
  );
  private readonly offlineSnapshotRepository = inject(
    OfflineSnapshotRepository,
  );
  readonly currentUser$ = this.user$.asObservable();
  readonly authState$ = this.authState$$.asObservable();
  public readonly currentUserSettings$ =
    new BehaviorSubject<UserSettings | null>(null);
  private readonly currentUserSignal = toSignal(this.user$, {
    initialValue: this.localStorageService.getItem<User>(
      CURRENT_USER_LOCAL_STORAGE,
    ),
  });
  private readonly authStateSignal = toSignal(this.authState$, {
    initialValue: 'unknown' as AuthState,
  });

  init$(): Observable<User | null> {
    const storedUser = this.localStorageService.getItem<User>(
      CURRENT_USER_LOCAL_STORAGE,
    );

    this.authState$$.next('unknown');

    return this.accountAPIService.getCurrentUser$().pipe(
      tap(user => {
        if (storedUser && storedUser.id !== user?.id) {
          this.clearOfflineSnapshots(storedUser.id);
        }

        this.setSessionState(user);
      }),
      switchMap(user =>
        this.initUserSettings$(user).pipe(
          take(1),
          catchError(error => {
            console.log(
              `Error getting user settings! Error: ${JSON.stringify(error?.error)}! Message: ${error?.message}`,
            );

            return of(user);
          }),
        ),
      ),
      catchError(error => {
        console.log(
          `Error getting current user! Error: ${JSON.stringify(error?.error)}! Message: ${error?.message}`,
        );

        if (storedUser && this.shouldPreserveCachedSession(error)) {
          this.setSessionState(storedUser);

          return of(storedUser);
        }

        if (storedUser) {
          this.clearOfflineSnapshots(storedUser.id);
        }
        this.setSessionState(null);

        return of(null);
      }),
    );
  }

  initPublicSession(): void {
    this.user$.next(null);
    this.currentUserSettings$.next(null);
    this.authState$$.next('anonymous');
  }

  get themeChanged$() {
    return this.themeChanged
      .asObservable()
      .pipe(filter((mode): mode is ThemeMode => mode !== null));
  }

  get $currentUser() {
    return this.currentUserSignal;
  }

  get $authState() {
    return this.authStateSignal;
  }

  private initUserSettings$(user: User | null): Observable<User | null> {
    if (!user?.personId) {
      this.currentUserSettings$.next(null);
      this.deleteUserSettingsFromLocalStorage();
      this.themeChanged.next('system');

      return of(user);
    }

    return this.getUserSettings$().pipe(
      tap((settings: UserSettings | null) =>
        this.currentUserSettings$.next(
          settings
            ? {
                ...settings,
                outkeep: normalizeOutkeepTableSettings(settings.outkeep),
              }
            : null,
        ),
      ),
      tap((settings: UserSettings | null) => this.saveToLocalStorage(settings)),
      map(() => user),
    );
  }

  public saveToLocalStorage(settings: UserSettings | null): void {
    const normalizedOutkeepSettings = settings?.outkeep
      ? normalizeOutkeepTableSettings(
          deepMerge(this.getStoredOutkeepSettings(), settings.outkeep),
        )
      : null;

    if (settings?.common?.theme) {
      this.localStorageService.setItem(
        TUI_THEME_DEFAULT_KEY,
        settings.common.theme,
      );
      this.themeChanged.next(settings.common.theme);
    }
    if (normalizedOutkeepSettings?.tableColumns) {
      this.localStorageService.setItem(
        X_TABLE_COLUMNS,
        normalizedOutkeepSettings.tableColumns,
      );
    }
    if (normalizedOutkeepSettings?.tableColumnsOrder) {
      this.localStorageService.setItem(
        X_TABLE_COLUMNS_ORDER,
        normalizedOutkeepSettings.tableColumnsOrder,
      );
    }
    if (normalizedOutkeepSettings?.rowsSettings) {
      this.localStorageService.setItem(
        X_TABLE_ROWS_SETTINGS,
        normalizedOutkeepSettings.rowsSettings,
      );
    }
    if (normalizedOutkeepSettings?.tableColumnsWidths) {
      this.localStorageService.setItem(
        X_TABLE_COLUMNS_WIDTHS,
        normalizedOutkeepSettings.tableColumnsWidths,
      );
    }
    if (normalizedOutkeepSettings?.rowsPerPage) {
      this.localStorageService.setItem(
        X_TABLE_ROWS_PER_PAGE,
        normalizedOutkeepSettings.rowsPerPage,
      );
    }

    if (normalizedOutkeepSettings?.mobileRequestsListCompactMode) {
      this.localStorageService.setItem(
        MOBILE_REQUESTS_LIST_COMPACT_MODE,
        normalizedOutkeepSettings.mobileRequestsListCompactMode,
      );
    }
  }

  updateCurrentUser$(updatedUserFields: UpdateUserPayload) {
    return this.accountAPIService.updateCurrentUser$(updatedUserFields);
  }

  getUserSettings$() {
    return this.accountAPIService.getUserSettings$().pipe(
      catchError(err => {
        if (err.status !== 404) {
          throw err;
        }

        return of(null);
      }),
    );
  }

  createOrUpdateUserSettings$(settings: Partial<UserSettings>) {
    return this.currentUserSettings$.pipe(
      map((currentSettings: UserSettings | null) =>
        deepMerge(currentSettings, settings),
      ),
      switchMap((settings: UserSettings | null) =>
        this.accountAPIService.createOrUpdateUserSettings$(settings || {}),
      ),
      catchError(err => {
        if (err.status !== 404) {
          throw err;
        }

        return of(null);
      }),
    );
  }

  resetUserSettings$(keys?: (keyof UserSettings)[]) {
    return this.accountAPIService.resetUserSettings$(keys).pipe(
      catchError(err => {
        if (err.status !== 404) {
          throw err;
        }

        return of(null);
      }),
      tap(() => this.deleteUserSettingsFromLocalStorage()),
    );
  }

  getCurrentUserCounterparties$(): Observable<Counterparty[]> {
    return this.accountAPIService.getCurrentUserCounterparties$();
  }

  public deleteUserSettingsFromLocalStorage() {
    this.localStorageService.removeItem(TUI_THEME_DEFAULT_KEY);
    this.localStorageService.removeItem(X_TABLE_COLUMNS);
    this.localStorageService.removeItem(X_TABLE_COLUMNS_ORDER);
    this.localStorageService.removeItem(X_TABLE_ROWS_SETTINGS);
    this.localStorageService.removeItem(MOBILE_REQUESTS_LIST_COMPACT_MODE);
    this.localStorageService.removeItem(X_TABLE_COLUMNS_WIDTHS);
    this.localStorageService.removeItem(X_TABLE_ROWS_PER_PAGE);
  }

  private getStoredOutkeepSettings(): OutkeepSettings {
    return {
      tableColumns: this.localStorageService.getItem(X_TABLE_COLUMNS),
      tableColumnsOrder: this.localStorageService.getItem(
        X_TABLE_COLUMNS_ORDER,
      ),
      rowsSettings: this.localStorageService.getItem(X_TABLE_ROWS_SETTINGS),
      tableColumnsWidths: this.localStorageService.getItem(
        X_TABLE_COLUMNS_WIDTHS,
      ),
      rowsPerPage: this.localStorageService.getItem(X_TABLE_ROWS_PER_PAGE),
      mobileRequestsListCompactMode: this.localStorageService.getItem(
        MOBILE_REQUESTS_LIST_COMPACT_MODE,
      ),
    };
  }

  logout(): void {
    this.isLoadingService.start();

    const currentUser = this.user$.value ?? this.currentUserSignal();

    if (currentUser) {
      this.clearOfflineSnapshots(currentUser.id);
    }

    this.setSessionState(null);
    this.authLoginService.showLogin();
    this.isLoadingService.stop();
  }

  private setSessionState(user: User | null): void {
    const previousUser = this.user$.value ?? this.currentUserSignal();

    if (previousUser && previousUser.id !== user?.id) {
      this.clearOfflineSnapshots(previousUser.id);
    }

    if (user) {
      this.localStorageService.setItem(
        CURRENT_USER_LOCAL_STORAGE,
        user,
        OFFLINE_SESSION_TTL_MINUTES,
      );
      this.user$.next(user);
      this.authState$$.next('authenticated');

      return;
    }

    this.localStorageService.removeItem(CURRENT_USER_LOCAL_STORAGE);
    this.user$.next(null);
    this.currentUserSettings$.next(null);
    this.authState$$.next('anonymous');
  }

  private shouldPreserveCachedSession(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse)) {
      return false;
    }

    return error.status === 0 || error.status >= 500;
  }

  /**
   * Удалить офлайн-данные пользователя: снимки заявок и кэш/черновики/ошибки регламентов.
   * Очереди регламентов не удаляются — они изолированы владельцем.
   */
  private clearOfflineSnapshots(userId: number): void {
    void this.offlineSnapshotRepository
      .clearUser(environment.xOutkeepInfrastructureEnv, userId)
      .catch(error => Sentry.captureException(error));
    void this.workflowStorageRepository
      .clearOwnerData({
        environment: environment.xOutkeepInfrastructureEnv,
        userId,
      })
      .catch(error => Sentry.captureException(error));
  }
}
